// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useEffect, useState, useCallback } from "react";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import type { IBackendClient } from "../../services/backendClient";
import { UnauthorizedError } from "../../services/backendClient";
import type { WizardStepProps } from "../EnrollmentWizard";
import { useAuth } from "../../auth/CognitoAuthProvider";

export interface IssueCertificateStepProps extends WizardStepProps {
  backendClient: IBackendClient;
}

export default function IssueCertificateStep({
  state,
  setState,
  onComplete,
  onError,
  backendClient,
}: IssueCertificateStepProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const { tokens, logout } = useAuth();

  const handleEnroll = useCallback(async () => {
    if (!tokens?.accessToken) {
      onError("Not authenticated. Please log in and try again.");
      return;
    }
    if (!state.csrPem || !state.yubiKeySerial) {
      onError("Missing CSR or YubiKey serial. Please go back and retry.");
      return;
    }

    setLoading(true);
    try {
      const response = await backendClient.enroll(
        state.csrPem,
        state.yubiKeySerial,
        tokens.accessToken,
      );

      setState((prev) => ({
        ...prev,
        certificatePem: response.certificate_pem,
        chainPem: response.chain_pem,
      }));
      onComplete();
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        logout();
        return;
      }
      onError(err instanceof Error ? err.message : "Certificate issuance failed.");
    } finally {
      setLoading(false);
    }
  }, [backendClient, tokens, state.csrPem, state.yubiKeySerial, setState, onComplete, onError, logout]);

  // Auto-trigger enrollment on mount
  const [triggered, setTriggered] = useState(false);
  useEffect(() => {
    if (!triggered) {
      setTriggered(true);
      void handleEnroll();
    }
  }, [triggered, handleEnroll]);

  if (loading) {
    return (
      <Container>
        <StatusIndicator type="loading">
          Issuing certificate…
        </StatusIndicator>
      </Container>
    );
  }

  return (
    <SpaceBetween size="m">
      <Container header={<Header variant="h3">Issue Certificate</Header>}>
        <SpaceBetween size="s">
          <p>
            Your CSR will be sent to the backend for certificate issuance via
            AWS Private CA.
          </p>
          <Button
            variant="primary"
            onClick={() => void handleEnroll()}
            loading={loading}
          >
            Issue Certificate
          </Button>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
